import { useState } from "react";
import { PhoneCall } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { FormField, formInputClass } from "./forms/fields";
import { sendContactForm, SUCCESS_MESSAGE } from "../lib/contactForm";

const initialForm = { name: "", email: "", phone: "", time: "", reason: "" };

export const CallbackModal = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!/^[0-9+\s]{7,}$/.test(form.phone)) e.phone = "Please enter a valid phone number.";
    if (!form.time.trim()) e.time = "Please tell us a preferred time.";
    if (!form.reason.trim()) e.reason = "Please tell us what the call is regarding.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (sending) return;
    if (!validate()) return;
    setSending(true);
    setSendError("");
    try {
      await sendContactForm({
        subject: "New EDT Callback Request",
        from_name: form.name,
        botcheck: honeypot,
        fields: {
          Name: form.name,
          email: form.email,
          Phone: form.phone,
          "Preferred Callback Time": form.time,
          Regarding: form.reason,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      setForm(initialForm);
      setErrors({});
      setSubmitted(false);
      setSending(false);
      setSendError("");
      setHoneypot("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="callback-modal"
        className="max-w-md border border-[rgba(201,162,75,0.3)] bg-[#111110] text-[#f2ede3] sm:rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#f2ede3]">
            Request a Callback
          </DialogTitle>
          <DialogDescription className="text-sm text-[#a09a8c]">
            Leave your details and we&apos;ll call you back at a time that suits you.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div data-testid="callback-success-message" className="py-6 text-center">
            <PhoneCall size={26} className="mx-auto text-[#c9a24b]" />
            <p className="mt-4 text-sm text-[#f2ede3]">{SUCCESS_MESSAGE}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-2 space-y-4" data-testid="callback-form">
            <input type="text" name="botcheck" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <FormField label="Name" error={errors.name} testId="callback-name">
              <input
                className={formInputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="callback-name-input"
              />
            </FormField>
            <FormField label="Email" error={errors.email} testId="callback-email">
              <input
                type="email"
                className={formInputClass}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="callback-email-input"
              />
            </FormField>
            <FormField label="Phone Number" error={errors.phone} testId="callback-phone">
              <input
                className={formInputClass}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="callback-phone-input"
              />
            </FormField>
            <FormField label="Preferred Callback Time" error={errors.time} testId="callback-time">
              <input
                className={formInputClass}
                placeholder="e.g. Weekday mornings"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="callback-time-input"
              />
            </FormField>
            <FormField label="What Is The Call Regarding?" error={errors.reason} testId="callback-reason">
              <textarea
                className={formInputClass}
                rows={3}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                data-testid="callback-reason-input"
              />
            </FormField>
            <button
              type="submit"
              disabled={sending}
              data-testid="callback-submit-button"
              className="mt-2 w-full bg-[#c9a24b] px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Request Callback"}
            </button>
            {sendError && (
              <p data-testid="callback-send-error" className="text-sm text-[#e08a6b]">
                {sendError}
              </p>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
